// StringsMixin.ts

import { Component, Vue } from 'vue-property-decorator';
import { AnalyticsGender } from '@/../../libs/ba-types/src';
import { Gender } from '@/../types/enums';
import { formatDatePrettyLong, formatDateHyphensYYYYMMDD, formatDateSlashesYYYYMMDD, formatDatePretty, formatDateMonthDay, formatDatePrettyShort, getTime, formatDateEval, formatDateAndTime, formatDate, formatDateLastModified, formatDateFilename } from '@/helpers/date';
import { snipEmail, snipUUID, linkify, isEmpty, isNotEmpty, isEmptyArray, isNotEmptyArray, title, fullName, heightStringToInches, isValidUrl } from '@/pipes';
import { genderText, genderTextPlural, genderShort, genderType, athleteGender } from '@/pipes';
import { capitalize, lowercase, uppercase, role, capitalizeCamelCase } from '@/pipes';
import { BaseSoccerPosition, DominantSide, Length, Mass } from '@/../types/enums';
import { formatPositionShort, formatDominantShort } from "@/helpers"
import { heightInFt, heightInCm, weightInLbs, weightInKg, heightStr, weightStr } from '@/pipes';

@Component
export class StringsMixin extends Vue {
	formatDateStd = formatDate;
	formatDate = formatDatePrettyLong;
	formatDatePretty = formatDatePretty;
	formatDateMonthDay = formatDateMonthDay;
	formatDatePrettyShort = formatDatePrettyShort;
	formatDatePrettyLong = formatDatePrettyLong;
	formatDateSlashesYYYYMMDD = formatDateSlashesYYYYMMDD;
	formatDateHyphens = formatDateHyphensYYYYMMDD;
	formatDateEval = formatDateEval;
	formatDateAndTime = formatDateAndTime;
	formatDateLastModified = formatDateLastModified;
	formatDateFilename = formatDateFilename;
	getTime = getTime;
	capitalize = capitalize;
	lowercase = lowercase;
	uppercase = uppercase;
	capitalizeCamelCase = capitalizeCamelCase;
	roleToStr = role;
	titleCase = title;
	formatPositionShort = formatPositionShort;
	genderType = genderType;
	athleteGender = athleteGender;

	IsEmpty(data: any): boolean {
		return isEmpty(data);
	}
	IsNotEmpty(data: any): boolean {
		return isNotEmpty(data);
	}
	IsEmptyArray(a: Array<any>): boolean {
		return isEmptyArray(a);
	}
	IsNotEmptyArray(a: Array<any>): boolean {
		return isNotEmptyArray(a);
	}
	SetIfEmpty(dest: any, value: string): boolean {
		if( isNotEmpty(dest) ) return false;
		dest = value;
		return true;
	}
	NormalizeString(input: string): string {
		return input.replace(/[\s_\-]/g, '').toLowerCase();
	}
	NormStringIncludes(input: string, s: string): boolean {
		return this.NormalizeString(input).includes(this.NormalizeString(s));
	}
	Snip(s?: string, max: number = 16, tail: boolean = true): string {
		if( !s ) return '';
		if( s.length <= max ) return s;
		return s.substring(0, max-(tail? 4:3)) + '...' + (tail? s.charAt(s.length-1) : '');
	}
	SnipUrl(url: string, len: number = -1): string {
		if( this.IsEmpty(url) ) return '';
		url = this.TrimUrl(url);
		const idxDot = url.lastIndexOf('.');
		const idxEnd = url.length;
		const idx = (idxEnd - idxDot) > 8? (idxEnd - 8) : idxDot;
		if( len < 0 ) {
			const doubleSlash = url.indexOf('//');
			var slash = url.indexOf('/', doubleSlash + 2);
			if( slash < 7 ) slash = idxEnd;
			len = slash;
		}
		if( len >= idx ) return url;
		return url.substring(0,len) + '...' + url.substring(idx,idxEnd);
	}
	SnipEmail(email: string, maxUsername: number = 9, maxDomain: number = 15): string {
		return snipEmail(email, maxUsername, maxDomain);
	}
	SnipAthleteId(athleteId: string, len: number = 8): string {
		return snipUUID(athleteId, len);
	}
	SnipId(id: string, len: number = 5): string {
		return snipUUID(id, len);
	}
	FullName(given_name: string, family_name: string): string {
		return fullName(given_name, family_name);
	}

	TrimUrl(url: string): string {
		if( this.IsEmpty(url) ) return '';
		if( url.charAt(url.length - 1) === '/' ) url = url.substring(0, url.length - 1);
		const tripleW = url.indexOf('www.');
		if( tripleW >= 0 ) return url.substring(tripleW+4);
		const doubleSlash = url.indexOf('//');
		if( doubleSlash >= 0 ) return url.substring(doubleSlash+2);
		return url;
	}

	GenderText(gender: Gender | AnalyticsGender): string {
		return genderText(gender);
	}
	GenderTextPlural(gender: Gender | AnalyticsGender, isAdult: boolean = false): string {
		return genderTextPlural(gender, {adult: isAdult});
	}
	GenderShort(gender: Gender | AnalyticsGender): string {
		return genderShort(gender);
	}

	PositionShort(pos: BaseSoccerPosition): string {
		return formatPositionShort(pos);
	}

	DominantSideShort(side: DominantSide): string {
		return formatDominantShort(side);
	}

	WeightStringToLbs(weightString: string): number | null {
		if( isEmpty(weightString) ) return undefined;

		const trimmedWeightString = weightString.trim();

		// Regex to capture the number and an optional unit
		// We'll prioritize explicit units first

		// --- 1. Handle Kilograms (kg, kgs, kilogram, kilograms) ---
		// Matches e.g., "68 kg", "68.5 kilograms"
		const kgMatch = trimmedWeightString.match(/^(\d+(\.\d+)?)\s*(?:kg|kgs|kilogram|kilograms)$/i);
		if (kgMatch) {
			const value = parseFloat(kgMatch[1]);
			if (!isNaN(value)) {
			// Convert kilograms to pounds (1 kg = 2.20462 lbs)
			return value * 2.20462;
			}
		}

		// --- 2. Handle Pounds (lbs, lb, pound, pounds, #) ---
		// Matches e.g., "150 lbs", "150#", "150.5 pounds"
		const lbsMatch = trimmedWeightString.match(/^(\d+(\.\d+)?)\s*(?:lbs|lb|pound|pounds|#)$/i);
		if (lbsMatch) {
			const value = parseFloat(lbsMatch[1]);
			if (!isNaN(value)) {
			return value; // Already in pounds
			}
		}

		// --- 3. Handle single number (default to pounds) ---
		// Matches e.g., "150"
		// This should be the last check to ensure explicit units are handled first.
		const singleNumberMatch = trimmedWeightString.match(/^(\d+(\.\d+)?)$/);
		if (singleNumberMatch) {
			const value = parseFloat(singleNumberMatch[1]);
			if (!isNaN(value)) {
			return value; // Assume pounds if no unit specified
			}
		}

		return undefined;
	}

	HeightInFt(height: number, heightUnit: Length = Length.CENTIMETER): string {
		return heightInFt(height, heightUnit);
	}
	HeightInCm(height: number, heightUnit: Length = Length.CENTIMETER): string {
		return heightInCm(height, heightUnit);
	}
	WeightInLbs(weight: number, weightUnit: Mass = Mass.KILOGRAM): string {
		return weightInLbs(weight, weightUnit);
	}
	WeightInKg(weight: number, weightUnit: Mass = Mass.KILOGRAM): string {
		return weightInKg(weight, weightUnit);
	}
	HeightStr(height: number, heightUnit: Length = Length.CENTIMETER): string {
		return heightStr(height, heightUnit);
	}
	WeightStr(weight: number, weightUnit: Mass = Mass.KILOGRAM): string {
		return weightStr(weight, weightUnit);
	}

	HeightStringToInches(heightString: string): number {
		return heightStringToInches(heightString);
	}

	KeyRepresents(key: string, label: string ): boolean {
		return this.NormalizeString(key).includes(this.NormalizeString(label));
	}

	linkify(link: string): string {
		return linkify(link);
	}

	formatRecord(item: Record<string,any>): any {
		if( typeof item.value === 'string' ) return item.value;
		else if( typeof item.value === 'number' ) return item.value;
		else if( Array.isArray(item.value) ) return item.value.join(' & ');
		else if( item.value instanceof Date ) return this.formatDatePrettyShort(item.value, true);
		else return item.value;
	}

	escapeCSV = (value: any): string => {
		const str = ( value instanceof Date )? this.formatDatePrettyShort(new Date(value), true) : String(value?? '-');            
		return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
	};

	IsValidUrl(url: string): boolean {
		return isValidUrl(url);
	}
}
